/**
 * AdminNews.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Self-contained admin shell — sidebar layout + news management in one file.
 * Route: /admin/news  (no separate AdminLayout needed)
 *
 * Lets an admin create, browse, and remove entries in the `news_articles`
 * table that powers the app's news feed.
 */

import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  LayoutDashboard, Users, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Shield, AlertCircle,
  Activity, CreditCard, MapPin, Menu, X, Newspaper,
  Plus, Search, Trash2, Loader2,
} from "lucide-react";

// ─── Injected styles ──────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);

    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);

    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --orange:       #fb923c;
    --orange-dim:   rgba(251,146,60,0.13);
    --blue:         #60a5fa;
    --blue-dim:     rgba(96,165,250,0.13);

    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;

    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
    --radius-xl: 22px;
  }

  html, body, #root {
    height: 100%;
    background: var(--bg-base);
    font-family: var(--font-sans);
    color-scheme: dark;
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }

  /* ── Noise texture overlay ── */
  .ad-shell {
    display: flex;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    background: var(--bg-base);
    position: relative;
  }
  .ad-shell::before {
    content: '';
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* ── Mobile overlay backdrop ── */
  .ad-overlay {
    display: none;
    position: fixed; inset: 0; z-index: 40;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(2px);
    opacity: 0;
    transition: opacity 0.28s ease;
    pointer-events: none;
  }
  .ad-overlay.open {
    display: block;
    opacity: 1;
    pointer-events: auto;
  }

  /* ── Sidebar ── */
  .ad-sidebar {
    width: var(--sidebar-w);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background: var(--bg-surface);
    border-right: 1px solid var(--border);
    position: relative;
    z-index: 50;
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
    will-change: transform;
  }
  .ad-sidebar::after {
    content: '';
    position: absolute;
    top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }

  /* ── Logo ── */
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px;
    height: var(--topbar-h);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
    position: relative;
  }
  .ad-logo-close {
    display: none;
    margin-left: auto;
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: none; cursor: pointer;
    align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
    flex-shrink: 0;
  }
  .ad-logo-close:hover { background: var(--rose-dim); color: var(--rose); }

  .ad-logo-icon {
    width: 32px; height: 32px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text { line-height: 1; }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; color: var(--text-primary);
    letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span {
    font-size: 9.5px; color: var(--text-muted); display: block;
    margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500;
  }

  /* ── Nav ── */
  .ad-nav {
    flex: 1; min-height: 0; overflow-y: auto; padding: 18px 10px;
    display: flex; flex-direction: column; gap: 22px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.18); border-radius: 99px; }
  .ad-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.35); }

  .ad-nav-section-label {
    font-size: 9px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: var(--text-muted);
    padding: 0 10px; margin-bottom: 5px;
  }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }

  .ad-nav-link {
    display: flex; align-items: center; gap: 9px;
    padding: 9px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease;
    position: relative;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light);
    font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: '';
    position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }

  /* ── User card ── */
  .ad-user-card {
    flex-shrink: 0;
    border-top: 1px solid var(--border);
    padding: 12px 10px;
    background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%);
  }
  .ad-user-inner {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px; border-radius: var(--radius-sm);
    transition: background 0.15s;
  }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light);
    flex-shrink: 0;
    box-shadow: 0 0 12px rgba(99,102,241,0.15);
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); box-shadow: 0 0 12px rgba(251,113,133,0.15); }

  /* ── Page area ── */
  .ad-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; min-width: 0; }

  /* ── Topbar ── */
  .ad-topbar {
    flex-shrink: 0;
    height: var(--topbar-h);
    background: rgba(10,10,22,0.92);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 20px;
    position: relative;
    gap: 10px;
  }
  .ad-topbar::after {
    content: '';
    position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .ad-topbar-left { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
  .ad-topbar-badge {
    display: flex; align-items: center; gap: 6px;
    padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light);
    letter-spacing: 0.5px; text-transform: uppercase; white-space: nowrap;
  }
  .ad-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-topbar-sub { font-size: 11px; color: var(--text-muted); margin-top: 1px; letter-spacing: 0.1px; }
  .ad-topbar-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

  /* ── Hamburger ── */
  .ad-hamburger {
    display: none;
    width: 36px; height: 36px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border);
    cursor: pointer; align-items: center; justify-content: center;
    color: var(--text-secondary); transition: all 0.15s; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.25); }

  .ad-pill-live {
    display: flex; align-items: center; gap: 7px;
    padding: 5px 12px; border-radius: 99px;
    background: var(--green-dim); border: 1px solid rgba(52,211,153,0.22);
    font-size: 11px; font-weight: 700; color: var(--green);
    letter-spacing: 0.3px; white-space: nowrap;
  }
  .ad-pulse {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--green);
    box-shadow: 0 0 6px var(--green);
    animation: pulse 2s infinite;
  }
  @keyframes pulse { 0%,100%{ opacity:1; transform:scale(1); } 50%{ opacity:0.55; transform:scale(0.75); } }

  .ad-refresh-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    font-size: 11.5px; font-weight: 500; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-refresh-btn:hover {
    background: var(--accent-dim); color: var(--accent-light);
    border-color: rgba(99,102,241,0.3);
    box-shadow: 0 0 12px rgba(99,102,241,0.1);
  }

  /* ── Scrollable main ── */
  .ad-main {
    flex: 1; overflow-y: auto; padding: 26px 28px;
    display: flex; flex-direction: column; gap: 26px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    overscroll-behavior-y: contain;
  }
  .ad-main::-webkit-scrollbar { width: 4px; }
  .ad-main::-webkit-scrollbar-track { background: transparent; }
  .ad-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* ── KPI grid ── */
  .ad-kpi-grid-a { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
  .ad-kpi-grid-b { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }

  .ad-kpi {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    padding: 20px;
    display: flex; flex-direction: column; gap: 14px;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
    text-decoration: none;
    position: relative; overflow: hidden;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-kpi::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--kpi-glow, transparent) 0%, transparent 60%);
    pointer-events: none; transition: opacity 0.22s;
  }
  .ad-kpi::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--kpi-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .ad-kpi:hover {
    border-color: rgba(255,255,255,0.1);
    box-shadow: 0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06);
    transform: translateY(-1px);
  }

  .ad-kpi-top { display: flex; align-items: flex-start; justify-content: space-between; }
  .ad-kpi-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); padding-top: 1px; }
  .ad-kpi-icon {
    width: 34px; height: 34px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  }
  .ad-kpi-value {
    font-size: 28px; font-weight: 700; color: var(--text-primary);
    letter-spacing: -1.2px; font-family: var(--font-mono); line-height: 1;
  }
  .ad-kpi-sub { display: flex; align-items: center; gap: 4px; font-size: 11px; margin-top: 5px; font-weight: 500; }
  .ad-kpi-sub.up { color: var(--green); }
  .ad-kpi-sub.down { color: var(--rose); }
  .ad-kpi-manage {
    display: flex; align-items: center; gap: 5px;
    font-size: 10.5px; font-weight: 600; letter-spacing: 0.2px;
    color: var(--text-muted); margin-top: auto; padding-top: 4px; transition: color 0.15s;
  }
  .ad-kpi:hover .ad-kpi-manage { color: var(--accent-light); }

  /* ── Alert pills ── */
  .ad-alerts { display: flex; flex-wrap: wrap; gap: 8px; }
  .ad-alert-pill {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 6px 14px; border-radius: 99px;
    font-size: 11.5px; font-weight: 600;
    text-decoration: none; border: 1px solid; transition: all 0.18s;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-alert-pill:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
  .ad-alert-pill.amber  { background: var(--amber-dim);  border-color: rgba(251,191,36,0.22);  color: var(--amber);  }
  .ad-alert-pill.indigo { background: var(--accent-dim); border-color: rgba(99,102,241,0.22);  color: var(--accent-light); }
  .ad-alert-pill.rose   { background: var(--rose-dim);   border-color: rgba(251,113,133,0.22); color: var(--rose);   }
  .ad-alert-pill.purple { background: var(--purple-dim); border-color: rgba(167,139,250,0.22); color: var(--purple); }

  /* ── Bottom grid ── */
  .ad-bottom-grid { display: grid; grid-template-columns: 1fr 1fr 320px; gap: 16px; }
  .ad-mid-grid    { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

  /* ── Panel ── */
  .ad-panel {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    overflow: hidden;
    display: flex; flex-direction: column;
    transition: border-color 0.2s;
  }
  .ad-panel:hover { border-color: var(--border-hi); }
  .ad-panel-header {
    padding: 15px 20px;
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
    background: linear-gradient(to bottom, rgba(255,255,255,0.02), transparent);
  }
  .ad-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.2px; }
  .ad-panel-link {
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; font-weight: 600; color: var(--accent-light); text-decoration: none;
    transition: all 0.15s; padding: 3px 8px; border-radius: 6px;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-panel-link:hover { background: var(--accent-dim); color: var(--text-primary); }

  /* ── Table ── */
  .ad-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  table.ad-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  table.ad-table thead tr { border-bottom: 1px solid var(--border); }
  table.ad-table th {
    padding: 9px 18px; text-align: left; font-size: 9px; font-weight: 700;
    letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-muted); white-space: nowrap;
  }
  table.ad-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  table.ad-table tbody tr:last-child { border-bottom: none; }
  table.ad-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  table.ad-table td { padding: 12px 18px; }

  /* ── Avatar cell ── */
  .ad-avatar-cell { display: flex; align-items: center; gap: 10px; }
  .ad-avatar {
    width: 30px; height: 30px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.18));
    border: 1.5px solid rgba(99,102,241,0.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
  }
  .ad-cell-name { font-size: 12.5px; font-weight: 500; color: var(--text-primary); }
  .ad-cell-email { font-size: 11px; color: var(--text-secondary); }
  .ad-cell-date { font-size: 10.5px; color: var(--text-muted); font-family: var(--font-mono); letter-spacing: -0.2px; }
  .ad-cell-amount { font-size: 13px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono); letter-spacing: -0.3px; }

  /* ── Status chip ── */
  .ad-chip {
    display: inline-flex; align-items: center;
    padding: 3px 9px; border-radius: 99px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: capitalize;
  }
  .ad-chip.active, .ad-chip.approved, .ad-chip.deposit    { background: var(--green-dim);  color: var(--green);  border: 1px solid rgba(52,211,153,0.18); }
  .ad-chip.pending, .ad-chip.open                         { background: var(--amber-dim);  color: var(--amber);  border: 1px solid rgba(251,191,36,0.18); }
  .ad-chip.suspended, .ad-chip.cancelled, .ad-chip.withdrawal { background: var(--rose-dim); color: var(--rose); border: 1px solid rgba(251,113,133,0.18); }
  .ad-chip.completed                                      { background: var(--accent-dim); color: var(--accent-light); border: 1px solid rgba(99,102,241,0.18); }
  .ad-chip.closed                                         { background: rgba(255,255,255,0.04); color: var(--text-muted); border: 1px solid rgba(255,255,255,0.06); }

  /* ── Activity feed ── */
  .ad-feed { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .ad-feed::-webkit-scrollbar { width: 0; }
  .ad-feed-item { display: flex; align-items: flex-start; gap: 11px; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  .ad-feed-item:last-child { border-bottom: none; }
  .ad-feed-item:hover { background: rgba(99,102,241,0.04); }
  .ad-feed-dot {
    width: 26px; height: 26px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.15));
    border: 1px solid rgba(99,102,241,0.2);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .ad-feed-body { min-width: 0; }
  .ad-feed-text { font-size: 11.5px; color: var(--text-secondary); line-height: 1.45; }
  .ad-feed-text strong { color: var(--text-primary); font-weight: 600; }
  .ad-feed-time { font-size: 10px; color: var(--text-muted); margin-top: 3px; font-family: var(--font-mono); }

  /* ── Quick actions ── */
  .ad-qa-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .ad-qa-btn {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 18px;
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-md); text-decoration: none;
    transition: all 0.2s; cursor: pointer; position: relative; overflow: hidden;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-qa-btn::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(99,102,241,0.04) 0%, transparent 60%);
    opacity: 0; transition: opacity 0.2s; pointer-events: none;
  }
  .ad-qa-btn:hover { border-color: rgba(99,102,241,0.22); box-shadow: 0 4px 20px rgba(0,0,0,0.3); transform: translateY(-1px); }
  .ad-qa-btn:hover::before { opacity: 1; }
  .ad-qa-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ad-qa-label { font-size: 12px; font-weight: 600; color: var(--text-secondary); flex: 1; transition: color 0.15s; }
  .ad-qa-btn:hover .ad-qa-label { color: var(--text-primary); }
  .ad-qa-arrow { color: var(--text-muted); transition: all 0.15s; }
  .ad-qa-btn:hover .ad-qa-arrow { color: var(--accent-light); transform: translateX(2px); }

  /* ── Section label ── */
  .ad-section-label {
    font-size: 11px; font-weight: 700; color: var(--text-secondary);
    letter-spacing: 0.6px; margin-bottom: 12px;
    display: flex; align-items: center; gap: 8px; text-transform: uppercase;
  }
  .ad-section-label::after { content: ''; flex: 1; height: 1px; background: linear-gradient(to right, var(--border), transparent); }

  /* ── Empty + skeleton ── */
  .ad-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 44px; color: var(--text-muted); gap: 8px; font-size: 12px; }
  .ad-skel { height: 10px; border-radius: 5px; background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(99,102,241,0.05) 50%, var(--bg-hover) 75%); background-size: 200% 100%; animation: shimmer 1.8s infinite; }
  @keyframes shimmer { 0%{ background-position: 200% 0; } 100%{ background-position: -200% 0; } }

  /* ── Status icon row ── */
  .ad-status-row { display: flex; align-items: center; gap: 6px; }
  .ad-kpi-divider { height: 0; }

  /* ════════════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════════════════ */

  /* ── Large desktop (≥1400px): wider sidebar, bigger grids ── */
  @media (min-width: 1400px) {
    :root { --sidebar-w: 252px; }
    .ad-kpi-grid-a { grid-template-columns: repeat(4, 1fr); }
    .ad-kpi-grid-b { grid-template-columns: repeat(4, 1fr); }
    .ad-main { padding: 30px 36px; }
    .ad-topbar { padding: 0 36px; }
  }

  /* ── Medium desktop (1024px–1399px): default layout ── */
  @media (max-width: 1280px) {
    .ad-bottom-grid { grid-template-columns: 1fr 1fr; }
    .ad-bottom-grid .ad-panel:nth-child(1) { grid-column: 1 / -1; }
  }

  /* ── Tablet landscape (900px–1023px) ── */
  @media (max-width: 1023px) {
    :root { --sidebar-w: 220px; }
    .ad-kpi-grid-a { grid-template-columns: repeat(2, 1fr); }
    .ad-kpi-grid-b { grid-template-columns: repeat(2, 1fr); }
    .ad-qa-grid { grid-template-columns: repeat(2, 1fr); }
    .ad-main { padding: 20px 20px; gap: 20px; }
    .ad-topbar { padding: 0 20px; }
  }

  /* ── Tablet portrait / small laptop (768px–899px) ── */
  @media (max-width: 899px) {
    .ad-mid-grid { grid-template-columns: 1fr; }
    .ad-bottom-grid { grid-template-columns: 1fr; }
    .ad-bottom-grid .ad-panel:nth-child(1) { grid-column: auto; }
    .ad-kpi-grid-a { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .ad-kpi-grid-b { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .ad-kpi-value { font-size: 24px; }
  }

  /* ── Mobile breakpoint (<768px): slide-in sidebar ── */
  @media (max-width: 767px) {
    :root { --topbar-h: 56px; }

    .ad-hamburger { display: flex; }
    .ad-logo-close { display: flex; }

    /* Sidebar becomes a fixed drawer */
    .ad-sidebar {
      position: fixed;
      top: 0; left: 0; bottom: 0;
      height: 100%; height: 100dvh;
      overflow: hidden;
      transform: translateX(-100%);
      box-shadow: 4px 0 32px rgba(0,0,0,0.6);
    }
    .ad-sidebar.open {
      transform: translateX(0);
    }

    .ad-topbar { padding: 0 16px; }
    .ad-topbar-sub { display: none; }
    .ad-pill-live span:not(.ad-pulse) { display: none; }
    .ad-pill-live { padding: 5px 9px; }

    .ad-main { padding: 16px; gap: 16px; }

    .ad-kpi-grid-a { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
    .ad-kpi-grid-b { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .ad-kpi { padding: 14px; gap: 10px; }
    .ad-kpi-value { font-size: 22px; letter-spacing: -0.8px; }
    .ad-kpi-label { font-size: 8.5px; }
    .ad-kpi-icon { width: 30px; height: 30px; }

    .ad-mid-grid { grid-template-columns: 1fr; }
    .ad-bottom-grid { grid-template-columns: 1fr; }
    .ad-bottom-grid .ad-panel:nth-child(1) { grid-column: auto; }

    .ad-qa-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .ad-qa-btn { padding: 12px 14px; gap: 10px; }
    .ad-qa-label { font-size: 11px; }

    .ad-refresh-btn span { display: none; }
    .ad-refresh-btn { padding: 6px 10px; }

    .ad-alerts { gap: 6px; }
    .ad-alert-pill { font-size: 10.5px; padding: 5px 11px; }

    table.ad-table th { padding: 8px 12px; }
    table.ad-table td { padding: 10px 12px; }
    .ad-panel-header { padding: 12px 14px; }
  }

  /* ── Small mobile (<480px): single column KPI cards ── */
  @media (max-width: 479px) {
    .ad-kpi-grid-a { grid-template-columns: 1fr 1fr; }
    .ad-kpi-grid-b { grid-template-columns: 1fr 1fr; }
    .ad-qa-grid { grid-template-columns: 1fr; }
    .ad-kpi-value { font-size: 20px; }
    .ad-topbar-title { font-size: 13px; }
    .ad-main { padding: 12px; gap: 14px; }
  }

  /* ── Very small (<360px) ── */
  @media (max-width: 359px) {
    .ad-kpi-grid-a { grid-template-columns: 1fr; }
    .ad-kpi-grid-b { grid-template-columns: 1fr; }
  }

  /* ════════════════════════════════════════════
     NEWS ADMIN — toolbar, cards, modal & form
  ════════════════════════════════════════════ */

  /* ── Toolbar ── */
  .ad-news-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .ad-search-box {
    display: flex; align-items: center; gap: 8px;
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-sm); padding: 8px 12px;
    flex: 1; min-width: 200px; max-width: 340px;
  }
  .ad-search-box svg { color: var(--text-muted); flex-shrink: 0; }
  .ad-search-box input {
    background: none; border: none; outline: none;
    font-family: var(--font-sans); font-size: 12.5px; color: var(--text-primary);
    width: 100%;
  }
  .ad-search-box input::placeholder { color: var(--text-muted); }

  .ad-btn-primary {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 16px; border-radius: var(--radius-sm);
    background: linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%);
    border: none; font-family: var(--font-sans);
    font-size: 12.5px; font-weight: 600; color: #fff;
    cursor: pointer; transition: all 0.18s;
    box-shadow: 0 2px 12px rgba(99,102,241,0.3);
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 4px 20px rgba(99,102,241,0.4); }
  .ad-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  /* ── Row action buttons ── */
  .ad-row-actions { display: flex; align-items: center; gap: 4px; }
  .ad-icon-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: 1px solid transparent;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-icon-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.2); }
  .ad-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Title cell ── */
  .ad-news-title { font-size: 12.5px; font-weight: 600; color: var(--text-primary); max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ad-news-tag {
    display: inline-flex; align-items: center;
    padding: 3px 9px; border-radius: 99px;
    background: rgba(255,255,255,0.05); border: 1px solid var(--border-hi);
    font-size: 10px; font-weight: 600; color: var(--text-secondary);
    text-transform: capitalize; white-space: nowrap;
  }
  .ad-impact-low    { background: var(--green-dim);  color: var(--green);  border: 1px solid rgba(52,211,153,0.18); }
  .ad-impact-medium { background: var(--amber-dim);  color: var(--amber);  border: 1px solid rgba(251,191,36,0.18); }
  .ad-impact-high   { background: var(--rose-dim);   color: var(--rose);   border: 1px solid rgba(251,113,133,0.18); }

  /* ── Modal ── */
  .ad-modal-backdrop {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(4,4,10,0.72);
    backdrop-filter: blur(4px);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 40px 16px;
    overflow-y: auto;
    animation: fadeIn 0.18s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .ad-modal {
    width: 100%; max-width: 620px;
    background: var(--bg-surface);
    border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg);
    box-shadow: 0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(99,102,241,0.08);
    flex-shrink: 0;
    animation: modalIn 0.2s cubic-bezier(0.16,1,0.3,1);
  }
  @keyframes modalIn { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .ad-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 22px; border-bottom: 1px solid var(--border);
  }
  .ad-modal-title { font-size: 14.5px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .ad-modal-close {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--bg-card); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-modal-close:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.2); }
  .ad-modal-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 16px; max-height: 62vh; overflow-y: auto; }
  .ad-modal-footer { display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid var(--border); }

  .ad-modal-error {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: var(--radius-sm);
    background: var(--rose-dim); border: 1px solid rgba(251,113,133,0.22);
    color: var(--rose); font-size: 12px; font-weight: 500;
  }

  /* ── Form ── */
  .ad-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .ad-field { display: flex; flex-direction: column; gap: 6px; }
  .ad-field label {
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
    color: var(--text-muted);
  }
  .ad-field label .req { color: var(--rose); margin-left: 2px; }
  .ad-field .hint { font-size: 10.5px; color: var(--text-muted); font-weight: 400; text-transform: none; letter-spacing: 0; margin-top: 2px; }

  .ad-input, .ad-textarea, .ad-select {
    width: 100%;
    background: var(--bg-card);
    border: 1px solid var(--border-hi);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-family: var(--font-sans);
    font-size: 12.5px;
    color: var(--text-primary);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .ad-input::placeholder, .ad-textarea::placeholder { color: var(--text-muted); }
  .ad-input:focus, .ad-textarea:focus, .ad-select:focus {
    border-color: rgba(99,102,241,0.5);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }
  .ad-textarea { resize: vertical; min-height: 90px; line-height: 1.5; font-family: var(--font-sans); }
  .ad-select { appearance: none; cursor: pointer; }

  .ad-btn-secondary {
    padding: 9px 16px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    font-family: var(--font-sans); font-size: 12.5px; font-weight: 600;
    color: var(--text-secondary); cursor: pointer; transition: all 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .ad-btn-secondary:hover { background: var(--bg-hover); color: var(--text-primary); }

  @media (max-width: 560px) {
    .ad-form-row { grid-template-columns: 1fr; }
    .ad-modal-backdrop { padding: 0; align-items: stretch; }
    .ad-modal { max-width: none; min-height: 100%; border-radius: 0; }
  }

  .ad-spin { animation: ad-spin 0.8s linear infinite; }
  @keyframes ad-spin { to { transform: rotate(360deg); } }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── nav config ──────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy            },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck       },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart},
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2       },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag     },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/news", label: "News", icon: Newspaper },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",        label: "Subscriptions",       icon: CreditCard },
      { to: "/admin/payment-addresses",    label: "Payment Addresses",   icon: MapPin     },
      { to: "/admin/market-subscriptions", label: "Market Subscriptions",icon: MapPin     },
    ],
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}
function fmtDateTime(str) {
  if (!str) return "—";
  const d = new Date(str);
  return `${fmtDate(str)} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}
// yyyy-MM-ddTHH:mm for a <input type="datetime-local"> defaulting to "now"
function nowForInput() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const IMPACT_LEVELS = ["low", "medium", "high"];

const EMPTY_FORM = {
  category: "",
  tab: "",
  title: "",
  body: "",
  source: "",
  impact_level: "",
  badge_label: "",
  badge_type: "",
  published_at: nowForInput(),
  markets_affected: "",
};

// ─── Sidebar link ────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}
    >
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function Empty({ message = "No news articles yet" }) {
  return (
    <div className="ad-empty">
      <AlertCircle size={18} />
      <span>{message}</span>
    </div>
  );
}

// ─── Skeleton rows ────────────────────────────────────────────────────────────
function SkeletonRows({ cols = 5, rows = 5 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j} style={{ padding: "12px 18px" }}>
          <div className="ad-skel" style={{ width: `${50 + (j * 17) % 40}%` }} />
        </td>
      ))}
    </tr>
  ));
}

// ─── Impact chip ──────────────────────────────────────────────────────────────
function ImpactChip({ level }) {
  if (!level) return <span className="ad-news-tag">—</span>;
  const cls = ["low", "medium", "high"].includes(level) ? `ad-impact-${level}` : "";
  return <span className={`ad-news-tag ${cls}`}>{level}</span>;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdminNews() {
  const navigate = useNavigate();

  // ── Sidebar open state (mobile) ───────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => { closeSidebar(); }, []);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // ── Session state ──────────────────────────────────────────────────────────
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // ── News data state ────────────────────────────────────────────────────────
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // ── Add-news modal state ───────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");

  // ── Session validation on mount + real-time auth listener ─────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s) {
        navigate("/admin/login", { replace: true });
      } else {
        setSession(s);
        setSessionLoading(false);
        loadArticles();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!s) {
        navigate("/admin/login", { replace: true });
      } else {
        setSession(s);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadArticles() {
    setLoading(true);
    const { data, error } = await supabase
      .from("news_articles")
      .select("*")
      .order("published_at", { ascending: false });
    if (!error) setArticles(data ?? []);
    setLoading(false);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
  }

  function openModal() {
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  }
  function closeModal() {
    if (saving) return;
    setModalOpen(false);
  }

  function updateField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleAddArticle(e) {
    e.preventDefault();
    setFormError("");

    if (!form.category.trim() || !form.tab.trim() || !form.title.trim() || !form.published_at) {
      setFormError("Category, tab, title, and published date are required.");
      return;
    }

    setSaving(true);

    const marketsAffected = form.markets_affected
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      category:         form.category.trim(),
      tab:              form.tab.trim(),
      title:            form.title.trim(),
      body:             form.body.trim() || null,
      source:           form.source.trim() || null,
      impact_level:     form.impact_level || null,
      badge_label:      form.badge_label.trim() || null,
      badge_type:       form.badge_type.trim() || null,
      published_at:     new Date(form.published_at).toISOString(),
      markets_affected: marketsAffected.length ? marketsAffected : null,
    };

    const { error } = await supabase.from("news_articles").insert(payload);

    setSaving(false);

    if (error) {
      setFormError(error.message || "Couldn't save this article. Try again.");
      return;
    }

    setModalOpen(false);
    loadArticles();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this article? This can't be undone.")) return;
    setDeletingId(id);
    const { error } = await supabase.from("news_articles").delete().eq("id", id);
    setDeletingId(null);
    if (!error) setArticles((prev) => prev.filter((a) => a.id !== id));
  }

  const filteredArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) =>
      [a.title, a.category, a.tab, a.source].some((v) => v?.toLowerCase().includes(q))
    );
  }, [articles, search]);

  // ── Session gate — never flash the page to unauthenticated users ───────────
  if (sessionLoading) {
    return (
      <>
        <StyleInjector />
        <div style={{
          height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--bg-base)", flexDirection: "column", gap: 14,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg,#6366f1,#a78bfa)",
            boxShadow: "0 0 24px rgba(99,102,241,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Shield size={16} color="#fff" />
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
            Verifying session…
          </div>
        </div>
      </>
    );
  }

  const adminEmail = session?.user?.email ?? "admin@OptimStock.io";
  const adminInitials = adminEmail.slice(0, 2).toUpperCase();

  return (
    <>
      <StyleInjector />
      <div className="ad-shell">

        {/* ── Mobile overlay backdrop ─────────────────────────────────── */}
        <div
          className={`ad-overlay${sidebarOpen ? " open" : ""}`}
          onClick={closeSidebar}
          aria-hidden="true"
        />

        {/* ── Sidebar ────────────────────────────────────────────────── */}
        <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="ad-logo">
            <div className="ad-logo-icon">
              <Shield size={13} color="#fff" />
            </div>
            <div className="ad-logo-text">
              <strong>OptimStock</strong>
              <span>Admin panel</span>
            </div>
            <button className="ad-logo-close" onClick={closeSidebar} aria-label="Close menu">
              <X size={14} />
            </button>
          </div>

          <nav className="ad-nav">
            {NAV_SECTIONS.map((section) => (
              <div key={section.label}>
                <p className="ad-nav-section-label">{section.label}</p>
                <div className="ad-nav-items">
                  {section.items.map((item) => (
                    <SidebarLink key={item.to} {...item} onClick={closeSidebar} />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="ad-user-card">
            <div className="ad-user-inner">
              <div className="ad-user-avatar">{adminInitials}</div>
              <div className="ad-user-info">
                <strong>Super Admin</strong>
                <span>{adminEmail}</span>
              </div>
              <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </aside>

        {/* ── Page area ──────────────────────────────────────────────── */}
        <div className="ad-page">

          {/* Topbar */}
          <header className="ad-topbar">
            <div className="ad-topbar-left">
              <button
                className="ad-hamburger"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={16} />
              </button>
              <div>
                <div className="ad-topbar-title">News</div>
                <div className="ad-topbar-sub">
                  {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>
            </div>
            <div className="ad-topbar-right">
              <button className="ad-refresh-btn" onClick={loadArticles}>
                <Activity size={12} /> <span>Refresh</span>
              </button>
            </div>
          </header>

          {/* Scrollable content */}
          <main className="ad-main">

            <section>
              <div className="ad-section-label">Articles</div>

              <div className="ad-panel">
                <div className="ad-panel-header" style={{ padding: "14px 18px" }}>
                  <div className="ad-news-toolbar" style={{ width: "100%" }}>
                    <div className="ad-search-box">
                      <Search size={14} />
                      <input
                        placeholder="Search title, category, tab, source…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <button className="ad-btn-primary" onClick={openModal}>
                      <Plus size={14} /> Add News
                    </button>
                  </div>
                </div>

                <MiniNewsTable
                  loading={loading}
                  articles={filteredArticles}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                />
              </div>
            </section>

          </main>
        </div>
      </div>

      {/* ── Add News modal ─────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="ad-modal-backdrop" onClick={closeModal}>
          <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modal-header">
              <div className="ad-modal-title">Add news article</div>
              <button className="ad-modal-close" onClick={closeModal} aria-label="Close">
                <X size={14} />
              </button>
            </div>

            <form onSubmit={handleAddArticle}>
              <div className="ad-modal-body">

                {formError && (
                  <div className="ad-modal-error">
                    <AlertCircle size={14} />
                    {formError}
                  </div>
                )}

                <div className="ad-form-row">
                  <div className="ad-field">
                    <label>Category<span className="req">*</span></label>
                    <input
                      className="ad-input"
                      placeholder="e.g. markets, economy, crypto"
                      value={form.category}
                      onChange={(e) => updateField("category", e.target.value)}
                    />
                  </div>
                  <div className="ad-field">
                    <label>Tab<span className="req">*</span></label>
                    <input
                      className="ad-input"
                      placeholder="e.g. top-stories, forex"
                      value={form.tab}
                      onChange={(e) => updateField("tab", e.target.value)}
                    />
                  </div>
                </div>

                <div className="ad-field">
                  <label>Title<span className="req">*</span></label>
                  <input
                    className="ad-input"
                    placeholder="Headline shown in the news feed"
                    value={form.title}
                    onChange={(e) => updateField("title", e.target.value)}
                  />
                </div>

                <div className="ad-field">
                  <label>Body</label>
                  <textarea
                    className="ad-textarea"
                    placeholder="Full article text"
                    value={form.body}
                    onChange={(e) => updateField("body", e.target.value)}
                  />
                </div>

                <div className="ad-form-row">
                  <div className="ad-field">
                    <label>Source</label>
                    <input
                      className="ad-input"
                      placeholder="e.g. Reuters, Bloomberg"
                      value={form.source}
                      onChange={(e) => updateField("source", e.target.value)}
                    />
                  </div>
                  <div className="ad-field">
                    <label>Impact level</label>
                    <select
                      className="ad-select"
                      value={form.impact_level}
                      onChange={(e) => updateField("impact_level", e.target.value)}
                    >
                      <option value="">— None —</option>
                      {IMPACT_LEVELS.map((lvl) => (
                        <option key={lvl} value={lvl}>{lvl[0].toUpperCase() + lvl.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ad-form-row">
                  <div className="ad-field">
                    <label>Badge label</label>
                    <input
                      className="ad-input"
                      placeholder="e.g. Breaking"
                      value={form.badge_label}
                      onChange={(e) => updateField("badge_label", e.target.value)}
                    />
                  </div>
                  <div className="ad-field">
                    <label>Badge type</label>
                    <input
                      className="ad-input"
                      placeholder="e.g. warning, info, success"
                      value={form.badge_type}
                      onChange={(e) => updateField("badge_type", e.target.value)}
                    />
                  </div>
                </div>

                <div className="ad-form-row">
                  <div className="ad-field">
                    <label>Published at<span className="req">*</span></label>
                    <input
                      type="datetime-local"
                      className="ad-input"
                      value={form.published_at}
                      onChange={(e) => updateField("published_at", e.target.value)}
                    />
                  </div>
                  <div className="ad-field">
                    <label>Markets affected</label>
                    <input
                      className="ad-input"
                      placeholder="e.g. EUR/USD, Gold, S&P 500"
                      value={form.markets_affected}
                      onChange={(e) => updateField("markets_affected", e.target.value)}
                    />
                    <span className="hint">Comma-separated</span>
                  </div>
                </div>

              </div>

              <div className="ad-modal-footer">
                <button type="button" className="ad-btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ad-btn-primary" disabled={saving}>
                  {saving ? <Loader2 size={14} className="ad-spin" /> : <Plus size={14} />}
                  {saving ? "Saving…" : "Publish article"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ─── News table ────────────────────────────────────────────────────────────────
function MiniNewsTable({ loading, articles, onDelete, deletingId }) {
  return (
    <div className="ad-table-wrap">
      <table className="ad-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Tab</th>
            <th>Impact</th>
            <th>Published</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows cols={6} rows={6} />
          ) : articles.length === 0 ? (
            <tr><td colSpan={6}><Empty /></td></tr>
          ) : (
            articles.map((a) => (
              <tr key={a.id}>
                <td><span className="ad-news-title" title={a.title}>{a.title}</span></td>
                <td><span className="ad-news-tag">{a.category}</span></td>
                <td><span className="ad-cell-email">{a.tab}</span></td>
                <td><ImpactChip level={a.impact_level} /></td>
                <td><span className="ad-cell-date">{fmtDateTime(a.published_at)}</span></td>
                <td>
                  <div className="ad-row-actions">
                    <button
                      className="ad-icon-btn"
                      onClick={() => onDelete(a.id)}
                      disabled={deletingId === a.id}
                      title="Delete article"
                    >
                      {deletingId === a.id ? <Loader2 size={13} className="ad-spin" /> : <Trash2 size={13} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}